import type { SectionColor } from "../risikoanalyseStep/types";
import type { ContractSectionKey } from "../contractDetail/masterSections";

export type ContractStepKind =
  | "kontakt"
  | "vertragsBasis"
  | "risikoinformation"
  | "summen"
  | "umfang"
  | "zahlung"
  | "summary";

/** Einzelvertrag | Sammelvertrag | Bündelvertrag */
export type VertragsartKey = "einzel" | "sammel" | "buendel";

/** Versicherer-Schlüssel (zur Erweiterung — heute nur Generali). */
export type VersichererKey = "generali" | "";

/** Produkt / Tarifierung — gilt nur für Einzelvertrag.
 *  Sammelvertrag und Bündelvertrag haben kein Produkt. */
export type TarifierungKey = "standardvertrag" | "rahmenvereinbarung" | "";

/** Volle Vorvertrag-Verzweigung analog KFZ/Immobilie. */
export type VorvertragInfo = {
  exists: "ja" | "nein" | "";
  isFremdvertrag?: "ja" | "nein" | "";
  existingContractId?: string;
  gesellschaft?: string;
  vertragsnummer?: string;
  alreadyCancelled?: "ja" | "nein" | "";
  kuendigungZum?: string;
  kuendigungsgrund?: string;
  shouldCancel?: "ja" | "nein" | "";
};

export type ContactSummary = {
  id: number;
  displayName: string;
  role?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

export type RiskOption = {
  id: number;
  name: string;
  riskType?: string;
  contactID?: number;
  contractID?: number;
  characteristics?: Record<string, string>;
};

export type SummenInfo = {
  versicherungssumme: string;
};

export type UmfangInfo = {
  selbstbeteiligung: string;
};

export type ZahlungInfo = {
  // Beitrag
  jahrespraemieBrutto: string;
  jahrespraemieNetto: string;
  versicherungssteuersatz: string;
  praemieJeZahlweise: string;
  // Vertragsdaten
  versicherungsbeginn: string;
  versicherungsablauf: string;
  hauptfaelligkeit: string;
  naechsteFaelligkeit: string;
  // Zahlweise
  zahlweise: string;
  // Zahlungsart
  zahlungsart: string;
  // Bankverbindung
  bankverbindung: string;
  // Rechnungsstellung
  rechnungsempfaenger: string;
  abweichenderRechnungsempfaenger: string;
  // Bemerkungen
  bemerkung: string;
  // Vertragsnummer-Felder (oben am Vertrag, separat)
  vertragsnummer: string;
  versicherungsnummer: string;
  zweig: string;
  risikoOrt: string;
};

export type ContractStepDef = {
  kind: ContractStepKind;
  key: string;
  title: string;
  description?: string;
};

export type ContractSectionDef = {
  key: ContractSectionKey;
  title: string;
  color: SectionColor;
  steps: ContractStepDef[];
};

export const VERTRAGSARTEN: ReadonlyArray<{
  key: VertragsartKey;
  label: string;
  description: string;
}> = [
  {
    key: "einzel",
    label: "Einzelvertrag",
    description: "Ein Vertrag mit einer Vertragsnummer für ein Risiko",
  },
  {
    key: "sammel",
    label: "Sammelvertrag",
    description: "Ein Vertrag mit einer Vertragsnummer für mehrere Risiken",
  },
  {
    key: "buendel",
    label: "Bündelvertrag",
    description: "Mehrere Verträge mit eigenen Vertragsnummern, gebündelt unter einem Bündel",
  },
];

export const VERSICHERER_OPTIONS: ReadonlyArray<{
  key: Exclude<VersichererKey, "">;
  label: string;
}> = [{ key: "generali", label: "Generali" }];

export type TarifierungIconKey = "standard" | "rahmen";

export const TARIFIERUNGEN: ReadonlyArray<{
  key: Exclude<TarifierungKey, "">;
  label: string;
  description: string;
  iconKey: TarifierungIconKey;
  availableFor: VertragsartKey[];
}> = [
  {
    key: "standardvertrag",
    label: "Standardvertrag",
    description: "Marktüblicher Tarif des gewählten Versicherers",
    iconKey: "standard",
    availableFor: ["einzel"],
  },
  {
    key: "rahmenvereinbarung",
    label: "Rahmenvereinbarung",
    description: "Vorverhandelte Konditionen mit dem Versicherer",
    iconKey: "rahmen",
    availableFor: ["einzel"],
  },
];

/** Tarifierung ist nur für Einzelvertrag relevant. */
export const requiresTarifierung = (vertragsart: VertragsartKey | ""): boolean =>
  vertragsart === "einzel";
